import { useState, useEffect, useRef, useCallback } from 'react';
import { broadcastActivity, onActivityReceived } from '@/utils/broadcastChannel';

/**
 * Activity events to track for user activity detection.
 * These events indicate the user is actively using the application.
 */
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'] as const;

/**
 * Throttle interval for activity updates to prevent performance issues.
 * Max 1 update per second as specified in WP-SESSION-3.
 */
const THROTTLE_MS = 1000;

/**
 * Hook options for activity tracking.
 */
export interface UseActivityTrackerOptions {
  /**
   * Whether activity tracking is enabled.
   * Should be false when user is not authenticated.
   */
  enabled: boolean;
}

/**
 * Return type for the activity tracker hook.
 */
export interface UseActivityTrackerResult {
  /**
   * Timestamp of the last detected user activity (Date.now() value).
   */
  lastActivity: number;
  /**
   * Manually update the last activity timestamp.
   * Useful when extending session via button click.
   */
  updateActivity: () => void;
}

/**
 * Hook that tracks user activity for session timeout detection.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * Tracks:
 * - Mouse events (mousedown, mousemove)
 * - Keyboard events (keydown)
 * - Touch events (touchstart)
 * - Scroll events
 *
 * Features:
 * - Throttles updates to max 1 per second for performance
 * - Broadcasts activity to other tabs via BroadcastChannel
 * - Listens for activity from other tabs
 * - Uses passive event listeners for performance
 *
 * @param options - Configuration options
 * @returns Activity tracking result with lastActivity timestamp and updateActivity function
 */
export function useActivityTracker({ enabled }: UseActivityTrackerOptions): UseActivityTrackerResult {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const lastUpdateRef = useRef(Date.now());

  /**
   * Update activity timestamp and broadcast to other tabs.
   * Throttled to max 1 update per second.
   */
  const updateActivity = useCallback(() => {
    const now = Date.now();
    // Throttle updates
    if (now - lastUpdateRef.current > THROTTLE_MS) {
      lastUpdateRef.current = now;
      setLastActivity(now);
      // Broadcast to other tabs
      broadcastActivity(now);
    }
  }, []);

  /**
   * Force update activity without throttling.
   * Used when user explicitly extends session.
   */
  const forceUpdateActivity = useCallback(() => {
    const now = Date.now();
    lastUpdateRef.current = now;
    setLastActivity(now);
    broadcastActivity(now);
  }, []);

  // Set up activity event listeners
  useEffect(() => {
    if (!enabled) return;

    // Add listeners for all activity events
    // Using passive: true for scroll and touch events for performance
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [enabled, updateActivity]);

  // Listen for activity broadcasts from other tabs
  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = onActivityReceived((timestamp) => {
      // Update local state if the received timestamp is newer
      if (timestamp > lastUpdateRef.current) {
        lastUpdateRef.current = timestamp;
        setLastActivity(timestamp);
      }
    });

    return unsubscribe;
  }, [enabled]);

  return {
    lastActivity,
    updateActivity: forceUpdateActivity,
  };
}

export default useActivityTracker;
